(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  if (root && root.document && root.UnoGiftCapCore) {
    const runtime = factory(root, root.UnoGiftCapCore);
    root.UnoGiftCap = runtime;
    runtime.boot();
  }
})(typeof window !== 'undefined' ? window : null, function createUnoGiftCapRuntime(win, core) {
  'use strict';

  const ENGINE_SOURCE = 'uno-gift-cap-v2';
  const baseConfig = core.buildConfig();
  let activePromise = null;
  let booted = false;

  function getPubSubEvents() {
    if (typeof PUB_SUB_EVENTS !== 'undefined') return PUB_SUB_EVENTS;
    return win.PUB_SUB_EVENTS || null;
  }

  function getSubscribe() {
    if (typeof subscribe === 'function') return subscribe;
    return typeof win.subscribe === 'function' ? win.subscribe : null;
  }

  function getPublish() {
    if (typeof publish === 'function') return publish;
    return typeof win.publish === 'function' ? win.publish : null;
  }

  async function fetchJson(url, options) {
    const response = await win.fetch(url, options);
    let payload = null;
    try { payload = await response.json(); } catch (_) {}
    if (!response.ok || (payload && payload.status && payload.status >= 400) || (payload && payload.errors)) {
      const message = payload?.description || payload?.message || payload?.errors || `HTTP ${response.status}`;
      throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
    }
    return payload;
  }

  const cartUrl = core.ajaxJsonUrl(win.routes?.cart_url || '/cart');
  const cartUpdateUrl = core.ajaxJsonUrl(win.routes?.cart_update_url || '/cart/update');
  const cartAddUrl = core.ajaxJsonUrl(win.routes?.cart_add_url || '/cart/add');

  function setAcceleratedCheckoutBlocked(blocked) {
    const ready = !blocked;
    win.document.documentElement.toggleAttribute('data-uno-gift-cap-checkout-ready', ready);
    win.document.querySelectorAll('.cart__dynamic-checkout-buttons.additional-checkout-buttons').forEach((node) => {
      node.setAttribute('aria-busy', blocked ? 'true' : 'false');
      if (typeof node.toggleAttribute === 'function') node.toggleAttribute('inert', blocked);
    });
  }

  function setCheckoutBusy(busy) {
    win.document.querySelectorAll('[name="checkout"], [data-uno-gift-checkout]').forEach((button) => {
      if ('disabled' in button) button.disabled = Boolean(busy);
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
    if (busy) setAcceleratedCheckoutBlocked(true);
  }

  function showError(error) {
    const isArabic = (win.document.documentElement.lang || '').toLowerCase().startsWith('ar');
    const message = isArabic
      ? 'تعذر تحديث الهدايا تلقائيًا. حاول مرة أخرى قبل إتمام الطلب.'
      : 'We could not update your gifts automatically. Please try again before checkout.';
    const target = win.document.getElementById('cart-errors') || win.document.getElementById('CartDrawer-CartErrors');
    if (target) target.textContent = message;
    if (win.console?.error) win.console.error('[UNO Gift Cap v2]', error);
  }

  async function fetchCart() {
    return fetchJson(cartUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
  }

  async function applyUpdates(updates) {
    if (!Object.keys(updates).length) return;
    await fetchJson(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });
  }

  async function applyAdds(adds) {
    if (!adds.length) return;
    await fetchJson(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: adds }),
    });
  }

  async function publishCart(cart) {
    if (!cart) return;
    const events = getPubSubEvents();
    const publishEvent = getPublish();
    if (!publishEvent || !events?.cartUpdate) return;
    await Promise.resolve(publishEvent(events.cartUpdate, { source: ENGINE_SOURCE, cartData: cart }));
  }

  async function reconcileInternal() {
    setCheckoutBusy(true);
    setAcceleratedCheckoutBlocked(true);
    let lastCart = null;
    let succeeded = false;

    try {
      for (let pass = 0; pass < 5; pass += 1) {
        const cart = await fetchCart();
        lastCart = cart;
        const sourceOrder = core.getPersistentSourceOrder(cart, baseConfig, win.localStorage);
        const config = core.buildConfig(sourceOrder);
        const reconciliation = core.buildReconciliation(cart, config);
        const hasUpdates = Object.keys(reconciliation.updates).length > 0;
        const hasAdds = reconciliation.adds.length > 0;

        if (!hasUpdates && !hasAdds) {
          await publishCart(cart);
          succeeded = true;
          return cart;
        }

        if (hasUpdates) await applyUpdates(reconciliation.updates);
        if (hasAdds) await applyAdds(reconciliation.adds);
      }

      const cart = await fetchCart();
      lastCart = cart;
      const sourceOrder = core.getPersistentSourceOrder(cart, baseConfig, win.localStorage);
      const finalCheck = core.buildReconciliation(cart, core.buildConfig(sourceOrder));
      if (Object.keys(finalCheck.updates).length || finalCheck.adds.length) {
        throw new Error('Gift cap reconciliation did not converge.');
      }

      await publishCart(cart);
      succeeded = true;
      return cart;
    } finally {
      setCheckoutBusy(false);
      setAcceleratedCheckoutBlocked(!succeeded);
      if (!succeeded && lastCart) {
        try { await publishCart(lastCart); } catch (_) {}
      }
    }
  }

  function reconcile() {
    if (activePromise) return activePromise;
    activePromise = reconcileInternal()
      .catch((error) => {
        showError(error);
        throw error;
      })
      .finally(() => { activePromise = null; });
    return activePromise;
  }

  function checkoutUrl() {
    const route = win.routes?.cart_url || '/cart';
    return route.replace(/\/cart\/?$/, '/checkout');
  }

  async function handleCheckout(event) {
    event.preventDefault();
    event.stopPropagation();
    setCheckoutBusy(true);
    setAcceleratedCheckoutBlocked(true);
    try {
      await reconcile();
      win.location.assign(checkoutUrl());
    } catch (_) {
      setCheckoutBusy(false);
      setAcceleratedCheckoutBlocked(true);
    }
  }

  function attach() {
    setAcceleratedCheckoutBlocked(true);

    win.document.addEventListener('click', (event) => {
      if (event.target?.closest?.('[name="checkout"], [data-uno-gift-checkout]')) handleCheckout(event);
    }, true);

    win.document.addEventListener('submit', (event) => {
      if (event.submitter?.name === 'checkout') handleCheckout(event);
    }, true);

    const events = getPubSubEvents();
    const subscribeEvent = getSubscribe();
    if (subscribeEvent && events?.cartUpdate) {
      subscribeEvent(events.cartUpdate, (event) => {
        if (event?.source === ENGINE_SOURCE) return;
        return reconcile().catch(() => {});
      });
    }

    reconcile().catch(() => {});
  }

  function boot() {
    if (booted) return;
    booted = true;
    if (win.document.readyState === 'loading') {
      win.document.addEventListener('DOMContentLoaded', attach, { once: true });
    } else {
      attach();
    }
  }

  return { ENGINE_SOURCE, reconcile, fetchCart, attach, boot };
});
